/**
 * Admin — Localization Editor
 *
 * Two views for managing i18n keys:
 *   1. Namespace-first: browse by namespace, see all keys with values across languages
 *   2. Language-first: pick a language, see untranslated keys, translate inline
 *
 * Global search across all keys and values.
 *
 * Reads live data from i18next resource store. Edits are applied to
 * the i18next instance in-memory (immediate effect) and can be exported
 * as JSON for file-level persistence.
 *
 * English only (admin console).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './AdminPages.module.css';
import i18n from '../../i18n/config';

/* ================================================================== */
/*  Constants                                                         */
/* ================================================================== */

const LANGUAGES = ['en', 'ar', 'fr', 'de', 'uk', 'sv', 'ru', 'es'] as const;
type LangCode = (typeof LANGUAGES)[number];

const LANG_LABELS: Record<LangCode, string> = {
  en: 'English',
  ar: 'Arabic',
  fr: 'French',
  de: 'German',
  uk: 'Ukrainian',
  sv: 'Swedish',
  ru: 'Russian',
  es: 'Spanish',
};

const NAMESPACES = [
  'common', 'leftNav', 'userProfile', 'theme', 'discover',
  'projectConfig', 'community', 'geography', 'kpi', 'pages',
  'chat', 'landing',
] as const;
type NSName = (typeof NAMESPACES)[number];

/* ================================================================== */
/*  Utility: flatten nested JSON to dotted key paths                  */
/* ================================================================== */

function flattenObj(obj: any, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, val] of Object.entries(obj || {})) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      Object.assign(result, flattenObj(val, fullKey));
    } else {
      result[fullKey] = String(val ?? '');
    }
  }
  return result;
}

/* ================================================================== */
/*  Hook: extract flattened locale data from i18next                  */
/* ================================================================== */

interface FlatEntry {
  namespace: NSName;
  key: string;           // dotted path e.g. "button.save"
  fullKey: string;       // "common:button.save"
  values: Record<LangCode, string>;
}

function useFlatEntries(): FlatEntry[] {
  return useMemo(() => {
    const entries: FlatEntry[] = [];
    const store = (i18n as any).store?.data || {};

    for (const ns of NAMESPACES) {
      // Flatten each language bundle ONCE per namespace (not per key)
      const flatByLang: Record<string, Record<string, string>> = {};
      for (const lang of LANGUAGES) {
        const langBundle = store[lang]?.[ns] || {};
        flatByLang[lang] = flattenObj(langBundle);
      }

      // Use English keys as the canonical set
      const flatEn = flatByLang['en'] || {};

      for (const key of Object.keys(flatEn)) {
        const values: Record<string, string> = {};
        for (const lang of LANGUAGES) {
          values[lang] = flatByLang[lang][key] ?? '';
        }
        entries.push({
          namespace: ns,
          key,
          fullKey: `${ns}:${key}`,
          values: values as Record<LangCode, string>,
        });
      }
    }
    return entries;
  }, []);
}

/* ================================================================== */
/*  View Mode Types                                                   */
/* ================================================================== */

type ViewMode = 'namespace' | 'language';

/* ================================================================== */
/*  Value Cell (inline editable)                                      */
/* ================================================================== */

function ValueCell({
  value,
  isEnglish,
  isUntranslated,
  onSave,
}: {
  value: string;
  isEnglish: boolean;
  isUntranslated: boolean;
  onSave: (newVal: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const handleSave = () => {
    if (draft !== value) {
      onSave(draft);
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') setEditing(false);
          }}
          autoFocus
          style={{
            flex: 1, padding: '4px 8px', fontSize: 12, fontFamily: 'inherit',
            border: '1px solid #3b82f6', borderRadius: 4, background: '#eff6ff',
            color: '#1a1a2e', outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={handleSave}
          style={{
            padding: '4px 10px', fontSize: 11, fontWeight: 600,
            background: '#3b82f6', color: '#fff', border: 'none',
            borderRadius: 4, cursor: 'pointer',
          }}
        >
          OK
        </button>
      </div>
    );
  }

  return (
    <div
      onClick={() => !isEnglish && setEditing(true)}
      style={{
        padding: '4px 8px', fontSize: 12, borderRadius: 4, minHeight: 24,
        cursor: isEnglish ? 'default' : 'pointer',
        background: isUntranslated ? 'rgba(245, 158, 11, 0.08)' : 'transparent',
        border: isUntranslated ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid transparent',
        color: isUntranslated ? '#d97706' : '#1a1a2e',
        fontStyle: isUntranslated ? 'italic' : 'normal',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 220,
      }}
      title={isEnglish ? value : `Click to edit: ${value}`}
    >
      {value || <span style={{ color: '#d1d5db' }}>—</span>}
    </div>
  );
}

/* ================================================================== */
/*  Namespace-First View                                              */
/* ================================================================== */

function NamespaceView({
  entries,
  search,
  visibleLangs,
  onUpdateValue,
}: {
  entries: FlatEntry[];
  search: string;
  visibleLangs: LangCode[];
  onUpdateValue: (ns: string, key: string, lang: string, value: string) => void;
}) {
  const [activeNs, setActiveNs] = useState<NSName>('common');

  const filtered = useMemo(() => {
    let result = entries.filter((e) => e.namespace === activeNs);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((e) =>
        e.key.toLowerCase().includes(q) ||
        Object.values(e.values).some((v) => v.toLowerCase().includes(q))
      );
    }
    return result;
  }, [entries, activeNs, search]);

  return (
    <div>
      {/* Namespace tabs */}
      <div style={{
        display: 'flex', gap: 4, flexWrap: 'wrap' as const, marginBottom: 16,
        paddingBottom: 12, borderBottom: '1px solid #e5e7eb',
      }}>
        {NAMESPACES.map((ns) => {
          const count = entries.filter((e) => e.namespace === ns).length;
          return (
            <button
              key={ns}
              type="button"
              onClick={() => setActiveNs(ns)}
              style={{
                padding: '5px 12px', fontSize: 12, fontWeight: activeNs === ns ? 700 : 400,
                borderRadius: 5, cursor: 'pointer',
                background: activeNs === ns ? '#3b82f6' : '#f3f4f6',
                color: activeNs === ns ? '#ffffff' : '#6b7280',
                border: activeNs === ns ? '1px solid #3b82f6' : '1px solid #e5e7eb',
              }}
            >
              {ns}
              <span style={{ fontSize: 10, marginInlineStart: 4, opacity: 0.7 }}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Key table */}
      <div style={{ overflowX: 'auto' as const }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ textAlign: 'start' as const, padding: '8px 10px', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' as const }}>
                Key
              </th>
              {visibleLangs.map((lang) => (
                <th key={lang} style={{
                  textAlign: 'start' as const, padding: '8px 10px', fontWeight: 600,
                  color: lang === 'en' ? '#3b82f6' : '#6b7280', whiteSpace: 'nowrap' as const,
                }}>
                  {LANG_LABELS[lang]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((entry) => (
              <tr key={entry.fullKey} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{
                  padding: '6px 10px', fontFamily: 'monospace', fontSize: 11,
                  color: '#6b7280', whiteSpace: 'nowrap' as const, maxWidth: 260,
                  overflow: 'hidden', textOverflow: 'ellipsis',
                }} title={entry.key}>
                  {entry.key}
                </td>
                {visibleLangs.map((lang) => {
                  const isUntranslated = lang !== 'en' && entry.values[lang].startsWith('[EN]');
                  return (
                    <td key={lang} style={{ padding: '4px 6px' }}>
                      <ValueCell
                        value={entry.values[lang]}
                        isEnglish={lang === 'en'}
                        isUntranslated={isUntranslated}
                        onSave={(newVal) => onUpdateValue(entry.namespace, entry.key, lang, newVal)}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p style={{ padding: 20, textAlign: 'center' as const, color: '#9ca3af', fontSize: 13 }}>
            No keys found{search ? ` matching "${search}"` : ''}.
          </p>
        )}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Language-First View                                               */
/* ================================================================== */

function LanguageView({
  entries,
  search,
  onUpdateValue,
}: {
  entries: FlatEntry[];
  search: string;
  onUpdateValue: (ns: string, key: string, lang: string, value: string) => void;
}) {
  const [activeLang, setActiveLang] = useState<LangCode>('fr');
  const [showOnly, setShowOnly] = useState<'all' | 'untranslated'>('untranslated');

  const filtered = useMemo(() => {
    let result = entries;
    if (showOnly === 'untranslated') {
      result = result.filter((e) => {
        const val = e.values[activeLang];
        return !val || val.startsWith('[EN]');
      });
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((e) =>
        e.fullKey.toLowerCase().includes(q) ||
        e.values['en'].toLowerCase().includes(q) ||
        e.values[activeLang].toLowerCase().includes(q)
      );
    }
    return result;
  }, [entries, activeLang, showOnly, search]);

  // Stats
  const totalKeys = entries.length;
  const untranslatedCount = entries.filter((e) => {
    const v = e.values[activeLang];
    return !v || v.startsWith('[EN]');
  }).length;
  const translatedPct = totalKeys > 0 ? Math.round(((totalKeys - untranslatedCount) / totalKeys) * 100) : 0;

  return (
    <div>
      {/* Language picker */}
      <div style={{
        display: 'flex', gap: 4, flexWrap: 'wrap' as const, marginBottom: 16,
        paddingBottom: 12, borderBottom: '1px solid #e5e7eb',
      }}>
        {LANGUAGES.filter((l) => l !== 'en').map((lang) => {
          const langUntranslated = entries.filter((e) => {
            const v = e.values[lang];
            return !v || v.startsWith('[EN]');
          }).length;
          return (
            <button
              key={lang}
              type="button"
              onClick={() => setActiveLang(lang)}
              style={{
                padding: '5px 12px', fontSize: 12, fontWeight: activeLang === lang ? 700 : 400,
                borderRadius: 5, cursor: 'pointer',
                background: activeLang === lang ? '#3b82f6' : '#f3f4f6',
                color: activeLang === lang ? '#ffffff' : '#6b7280',
                border: activeLang === lang ? '1px solid #3b82f6' : '1px solid #e5e7eb',
              }}
            >
              {LANG_LABELS[lang]}
              {langUntranslated > 0 && (
                <span style={{
                  fontSize: 10, marginInlineStart: 4,
                  color: activeLang === lang ? 'rgba(255,255,255,0.7)' : '#f59e0b',
                }}>
                  ({langUntranslated})
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Stats + filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>
            {LANG_LABELS[activeLang]} — {translatedPct}% translated
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>
            {untranslatedCount} of {totalKeys} keys need translation
          </div>
          {/* Progress bar */}
          <div style={{
            marginTop: 6, height: 6, borderRadius: 3, background: '#f3f4f6',
            overflow: 'hidden', maxWidth: 300,
          }}>
            <div style={{
              height: '100%', borderRadius: 3,
              width: `${translatedPct}%`,
              background: translatedPct === 100 ? '#22c55e' : translatedPct > 50 ? '#3b82f6' : '#f59e0b',
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {(['all', 'untranslated'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setShowOnly(mode)}
              style={{
                padding: '5px 12px', fontSize: 11, fontWeight: showOnly === mode ? 700 : 400,
                borderRadius: 4, cursor: 'pointer',
                background: showOnly === mode ? '#1a1a2e' : '#f3f4f6',
                color: showOnly === mode ? '#ffffff' : '#6b7280',
                border: showOnly === mode ? '1px solid #1a1a2e' : '1px solid #e5e7eb',
              }}
            >
              {mode === 'all' ? 'All Keys' : 'Untranslated Only'}
            </button>
          ))}
        </div>
      </div>

      {/* Translation table */}
      <div style={{ overflowX: 'auto' as const }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ textAlign: 'start' as const, padding: '8px 10px', fontWeight: 700, color: '#374151', width: 140 }}>
                Namespace
              </th>
              <th style={{ textAlign: 'start' as const, padding: '8px 10px', fontWeight: 700, color: '#374151' }}>
                Key
              </th>
              <th style={{ textAlign: 'start' as const, padding: '8px 10px', fontWeight: 600, color: '#3b82f6' }}>
                English (reference)
              </th>
              <th style={{ textAlign: 'start' as const, padding: '8px 10px', fontWeight: 600, color: '#6b7280' }}>
                {LANG_LABELS[activeLang]}
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((entry) => {
              const isUntranslated = !entry.values[activeLang] || entry.values[activeLang].startsWith('[EN]');
              return (
                <tr key={entry.fullKey} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{
                    padding: '6px 10px', fontSize: 11, color: '#9ca3af', fontWeight: 500,
                  }}>
                    {entry.namespace}
                  </td>
                  <td style={{
                    padding: '6px 10px', fontFamily: 'monospace', fontSize: 11,
                    color: '#6b7280', maxWidth: 220, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
                  }} title={entry.key}>
                    {entry.key}
                  </td>
                  <td style={{ padding: '4px 6px', fontSize: 12, color: '#1a1a2e' }}>
                    {entry.values['en']}
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <ValueCell
                      value={entry.values[activeLang]}
                      isEnglish={false}
                      isUntranslated={isUntranslated}
                      onSave={(newVal) => onUpdateValue(entry.namespace, entry.key, activeLang, newVal)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p style={{ padding: 20, textAlign: 'center' as const, color: '#9ca3af', fontSize: 13 }}>
            {showOnly === 'untranslated'
              ? `All keys are translated for ${LANG_LABELS[activeLang]}!`
              : `No keys found${search ? ` matching "${search}"` : ''}.`}
          </p>
        )}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Main Page                                                         */
/* ================================================================== */

export default function AdminLocalization() {
  const [viewMode, setViewMode] = useState<ViewMode>('namespace');
  const [search, setSearch] = useState('');
  const [visibleLangs, setVisibleLangs] = useState<LangCode[]>(['en', 'fr', 'de', 'es']);
  const [editCount, setEditCount] = useState(0);

  const entries = useFlatEntries();

  // Stats
  const totalKeys = entries.length;

  const handleUpdateValue = useCallback((ns: string, key: string, lang: string, value: string) => {
    // Write to i18next resource store in-memory
    // Key is dotted path like "button.save" — i18next uses it directly with namespace
    try {
      i18n.addResource(lang, ns, key, value);
      setEditCount((c) => c + 1);
    } catch (err) {
      console.warn('[AdminLocalization] Failed to update resource:', err);
    }
  }, []);

  const handleExportJson = useCallback((lang: string, ns: string) => {
    const store = (i18n as any).store?.data || {};
    const bundle = store[lang]?.[ns] || {};
    const json = JSON.stringify(bundle, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${ns}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  return (
    <div>
      <h1 className={styles.pageTitle}>Localization Editor</h1>
      <p className={styles.pageSubtitle}>
        Browse and edit translation keys across all {LANGUAGES.length} languages
        and {NAMESPACES.length} namespaces ({totalKeys} total keys).
        {editCount > 0 && (
          <span style={{ marginInlineStart: 8, color: '#f59e0b', fontWeight: 600 }}>
            {editCount} edit{editCount !== 1 ? 's' : ''} this session (in-memory only)
          </span>
        )}
      </p>

      {/* ── Search + View Mode Toggle ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0 20px' }}>
        {/* Search */}
        <div style={{ flex: 1, maxWidth: 400 }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search keys and values..."
            spellCheck={false}
            style={{
              width: '100%', padding: '8px 12px', fontSize: 13,
              border: '1px solid #d1d5db', borderRadius: 6,
              background: '#f9fafb', color: '#1a1a2e',
            }}
          />
        </div>

        {/* View mode toggle */}
        <div style={{ display: 'flex', gap: 4 }}>
          {([
            { mode: 'namespace' as ViewMode, label: 'By Namespace' },
            { mode: 'language' as ViewMode, label: 'By Language' },
          ]).map(({ mode, label }) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              style={{
                padding: '7px 16px', fontSize: 12, fontWeight: viewMode === mode ? 700 : 400,
                borderRadius: 6, cursor: 'pointer',
                background: viewMode === mode ? '#1a1a2e' : '#f3f4f6',
                color: viewMode === mode ? '#ffffff' : '#6b7280',
                border: viewMode === mode ? '1px solid #1a1a2e' : '1px solid #d1d5db',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Language visibility (namespace view only) */}
        {viewMode === 'namespace' && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
            {LANGUAGES.map((lang) => {
              const isVisible = visibleLangs.includes(lang);
              return (
                <button
                  key={lang}
                  type="button"
                  onClick={() => {
                    if (isVisible && visibleLangs.length > 1) {
                      setVisibleLangs(visibleLangs.filter((l) => l !== lang));
                    } else if (!isVisible) {
                      setVisibleLangs([...visibleLangs, lang]);
                    }
                  }}
                  style={{
                    padding: '4px 8px', fontSize: 10, fontWeight: 600,
                    borderRadius: 4, cursor: 'pointer',
                    background: isVisible ? '#3b82f6' : '#f3f4f6',
                    color: isVisible ? '#ffffff' : '#9ca3af',
                    border: isVisible ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                  }}
                >
                  {lang.toUpperCase()}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Info banner ── */}
      <div style={{
        padding: '10px 16px', borderRadius: 6, marginBottom: 20,
        background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 12, color: '#1d4ed8',
      }}>
        Edits are applied in-memory for this session. To persist changes, export the
        namespace JSON and replace the corresponding file in <code style={{
          fontSize: 11, background: '#dbeafe', padding: '1px 4px', borderRadius: 3,
        }}>src/i18n/locales/&lt;lang&gt;/</code>.
      </div>

      {/* ── View content ── */}
      <div style={{
        background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8,
        padding: 20,
      }}>
        {viewMode === 'namespace' ? (
          <NamespaceView
            entries={entries}
            search={search}
            visibleLangs={visibleLangs}
            onUpdateValue={handleUpdateValue}
          />
        ) : (
          <LanguageView
            entries={entries}
            search={search}
            onUpdateValue={handleUpdateValue}
          />
        )}
      </div>

      {/* ── Export section ── */}
      <div style={{
        marginTop: 24, padding: 20, background: '#f8fafc',
        border: '1px solid #e5e7eb', borderRadius: 8,
      }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>
          Export
        </h3>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6b7280' }}>
          Download the current (including in-memory edits) namespace JSON for a language.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
          {LANGUAGES.map((lang) => (
            <div key={lang} style={{
              display: 'flex', flexDirection: 'column' as const, gap: 4,
              padding: 10, background: '#ffffff', border: '1px solid #e5e7eb',
              borderRadius: 6, minWidth: 100,
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>
                {LANG_LABELS[lang]}
              </span>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const }}>
                {NAMESPACES.map((ns) => (
                  <button
                    key={ns}
                    type="button"
                    onClick={() => handleExportJson(lang, ns)}
                    style={{
                      padding: '2px 6px', fontSize: 9, fontWeight: 500,
                      background: '#f3f4f6', color: '#6b7280',
                      border: '1px solid #e5e7eb', borderRadius: 3,
                      cursor: 'pointer', whiteSpace: 'nowrap' as const,
                    }}
                    title={`Export ${lang}/${ns}.json`}
                  >
                    {ns}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
